/**
 * This file will contain all the util functions to be utilized in the application.
 * */

import { FDProduct } from "../interface/fd";
import { apiBasedSubDomains, subDomains } from "./constants";

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        icm?: any;
    }
}

export const scrollToTop = (): void => {
    window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
    });
};

export const getQueryParams = (url: string): { [key: string]: string | undefined } => {
    const urlParams = new URLSearchParams(new URL(url).search);
    const params: { [key: string]: string | undefined } = {};
    urlParams.forEach((value, key) => {
        params[key] = value;
    });
    return params;
};

export const getCookie = (name: string): string | null => {
    if (typeof document === "undefined") return null;
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
};

export const setCookie = (name: string, value: string, days: number): void => {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = "expires=" + d.toUTCString();
    document.cookie = `${name}=${value}; ${expires}; path=/`;
};

export const deleteCookie = (name: string): void => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
};

export const formatIndianCurrency = (amount: number): string => {
    const formatter = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    });

    return formatter.format(amount);
};

export const toTitleCase = (input: string): string => {
    return input
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

export const DOBFormatter = (date: string): Date => {
    // Split the input date string by hyphen
    const [day, month, year] = date.split("-");
    return new Date(`${year}-${month}-${day}`);
};

export const encodeUrl = (url: string): string => {
    return encodeURIComponent(url);
};

export const generateUUID = () => crypto.randomUUID();

export const getHighestReturns = (data: FDProduct[]) => {
    // Initialize a variable to keep track of the highest returns
    let highestReturns = 0; // Start with a very low value

    // Iterate over each object in the array
    data?.forEach((item: FDProduct) => {
        if (item.isActive) {
            const totalReturns =
                Number(item.maxReturns.returns) + Number(item.maxReturns.seniorCitizenBenefits);

            // Update highestReturns if the current totalReturns is greater
            if (totalReturns > highestReturns) {
                highestReturns = totalReturns;
            }
        }
    });

    return highestReturns;
};

export const handleAppDownload = () => {
    const isAndroid = navigator.userAgent.match(/Android/i);
    const isIOS = navigator.userAgent.match(/iPhone|iPad|iPod/i);

    if (isAndroid) {
        window.location.href = process.env.ICM_PLAYSTORE_URL || "";
    } else if (isIOS) {
        window.location.href = process.env.ICM_IOS_APP_URL || "";
    } else {
        window.open(process.env.ICM_PLAYSTORE_URL || "", "_blank");
    }
};

/**
 * @param url string
 * @param hasProtocol boolean -- includes protocol in search
 * @returns string -- a clean url with domain name with optional protocol
 */
export const getCleanDomain = (url: string, hasProtocol: boolean = false): string => {
    const protocolPattern = hasProtocol ? "((https|http)?:\\/\\/)" : "";
    const regExp = new RegExp(`^${protocolPattern}([^/]+)`);
    const match = url.match(regExp);
    const matchedDomain = match ? (hasProtocol ? match[0] : match[match.length - 1]) : "";

    return matchedDomain;
};

export const getReleventDomain = (/* protocol?: boolean */) => {
    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    const icmSubdomains = process.env.ICM_SUB_DOMAINS?.replace(/'/gim, "").split(",");
    const subdomain = String(icmSubdomains?.find((sub) => sub?.trim() === hostname)?.trim());
    const url = getCompleteUrl(subdomain);
    // console.log("#########\n", hostname, "\n return url: ", url, "\n icm-domains: ", icmSubdomains);
    return url;
};

export const getSubDomainApi = (url: string): string => {
    let result = apiBasedSubDomains.NEUTRAL;
    // console.log(">> ", url);

    Object.entries(subDomains).forEach((subdomain) => {
        if (subdomain[1] === url) {
            Object.keys(apiBasedSubDomains).forEach((api) => {
                if (subdomain[0] === api) {
                    result = apiBasedSubDomains[api as keyof typeof apiBasedSubDomains];
                }
            });
        }
    });

    // console.log("subDomainApi ", result);
    return result;
};

// protocol + host + port.
export const getCompleteUrl = (subdomain: string) => {
    // debugger;
    let url = "";
    let port = "";
    const host = subdomain.split(":")[0];

    if (typeof window !== "undefined") {
        port = window.location.port;
    }
    if (host?.includes("localhost") || host?.includes("home.icm.com")) {
        url = "http://" + subdomain + (port ? ":" + port : "");
    } else {
        url = "https://" + subdomain;
    }
    return url;
};

export const formatNumberWithCommas = (number: number): string => {
    const x = number.toString().split(".");
    const integerPart = x[0];
    const decimalPart = x.length > 1 ? "." + x[1] : "";
    const lastThreeDigits = integerPart.slice(-3);
    const otherDigits = integerPart.slice(0, -3);
    const formattedNumber =
        otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") +
        (otherDigits ? "," : "") +
        lastThreeDigits +
        decimalPart;
    return formattedNumber;
};

export const timeAgo = (timestamp: number): string => {
    const now = Date.now();
    const difference = timestamp - now;
    const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

    const hours = Math.round(difference / (1000 * 60 * 60));

    // If difference is more than 24 hours, show date in DD MMM YYYY format
    if (Math.abs(hours) > 24) {
        return new Date(timestamp).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    }

    // For less than 24 hours, show relative time
    const seconds = Math.round(difference / 1000);
    const minutes = Math.round(seconds / 60);

    if (Math.abs(hours) > 0) return rtf.format(hours, "hour");
    if (Math.abs(minutes) > 0) return rtf.format(minutes, "minute");
    return rtf.format(seconds, "second");
};

export const trimText = (text: string, len: number) => {
    if (text.length > len) {
        return text.substring(0, len) + "...";
    }
    return text;
};

/**
 * X-partner
 */
export const isMTDomain = () => {
    const MT = String(process.env.MT_DOMAIN)?.trim();
    console.log("MT: ", MT);

    if (MT && MT !== "" && MT !== "undefined" && MT !== "null") {
        return true;
    }
    return false;
};

export const getXPartner = () => {
    const defaultXPartner = "1:1";
    const partnerMT = String(process.env.MT_DOMAIN).trim();
    let xPartner;

    if (typeof window !== "undefined") {
        xPartner = window.sessionStorage.getItem("partner");
    }

    if (
        (!xPartner || xPartner == "" || xPartner == "null") &&
        typeof partnerMT !== "undefined" &&
        partnerMT !== "undefined" &&
        partnerMT != ""
    ) {
        setXPartner("1", "1");
        xPartner = defaultXPartner;
    }
    return xPartner ?? "";
};

export const setXPartner = (p_value: string, sp_value: string) => {
    if (typeof window !== "undefined") {
        if (sp_value && sp_value !== "Empty selection") {
            return window.sessionStorage.setItem("partner", p_value + ":" + sp_value);
        }
        return window.sessionStorage.setItem("partner", p_value);
    }
};

export const removeXPartner = () => {
    if (typeof window !== "undefined") {
        return window.sessionStorage.removeItem("partner");
    }
};

export const setXPartnerfromQueryString = () => {
    if (!isMTDomain()) {
        return;
    }
    const searchParams =
        typeof window !== "undefined"
            ? Object.fromEntries(new URLSearchParams(window.location.search))
            : {};
    const partner = getXPartner().split(":");
    const xPartner = searchParams["xPartner"] ?? "";
    const xSubPartner = searchParams["xSubPartner"] ?? "";

    if (partner[0] == xPartner && partner[1] == xSubPartner) {
        //
    } else {
        console.log("xPartner:xSubPartner Not matched: ", partner);
        if (xPartner && xSubPartner) {
            setXPartner(String(xPartner), String(xSubPartner));
        }
    }
    console.log("xPartner:xSubPartner= ", getXPartner());
};
/* X-partner */

/**
 * redirectTo..
 * 1. To access post login page user needs to login first.
 * 2. Set `redirectTo` in storage. Redirect user to login page.
 * 3. Once logged in than redirect user back to `redirectTo`.
 */
export const setRedirectTo = (url: string) => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("redirectTo", String(url));
};

export const getRedirectTo = () => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem("redirectTo");
};

export const clearRedirectTo = () => {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem("redirectTo");
};

export const isValidRedirectTo = (val: unknown) => {
    if (val && String(val)?.trim()) {
        return true;
    } else {
        return false;
    }
};

export const formatToReadableDate = (dateStr: string) => {
    const [month, day, year] = dateStr.split("/").map(Number);

    const suffix = (d: number) => {
        if (d > 3 && d < 21) return "th";
        switch (d % 10) {
            case 1:
                return "st";
            case 2:
                return "nd";
            case 3:
                return "rd";
            default:
                return "th";
        }
    };

    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const formattedDay = `${day}${suffix(day)}`;
    const formattedMonth = monthNames[month - 1];
    const shortYear = `'${year?.toString()?.slice(-2)}`;

    return `${formattedDay} ${formattedMonth}${shortYear}`;
};

export const wrapEmojis = (text: string) => {
    const emojiRegex = /(\p{Extended_Pictographic}|\p{Emoji_Presentation}|\p{Emoji})/gu;
    return text.replace(emojiRegex, (emoji) => {
        return `<span class="emoji-force">${emoji}</span>`;
    });
};

export const delay = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

export const isIOS = (): boolean => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return false;
    }

    // Check using userAgent only, avoiding deprecated platform property
    return /iPad|iPhone|iPod|Mac|Macintosh|Darwin/i.test(navigator.userAgent);
};

export const getBannerLinkInfo = (
    url?: string,
): { isOtherDomain: boolean; normalizedUrl: string } => {
    if (!url) {
        return { isOtherDomain: false, normalizedUrl: "" };
    }

    const normalizedUrl = url.startsWith("http") ? url : `https://${url}`;

    return {
        isOtherDomain: !normalizedUrl.startsWith(process.env.ICM_URL as string),
        normalizedUrl,
    };
};
